import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, FolderKanban, Search, ChevronUp, ChevronDown, ChevronsUpDown, FileText, Users, ListTodo, AlertTriangle, Eye } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import { t } from '@/i18n/ro';
import { apiGet } from '@/services/api';
import { canCreateProject } from '@/services/projects.service';
import {
  Button,
  Card,
  LoadingState,
  EmptyState,
  Input,
} from '@/components/common';
import type { HalCollection } from '@/types';
import { SearchableSelect } from '../common/SearchableSelect';

interface ProjectMember {
  id: number;
  name: string;
  roles: string[];
}

interface ProjectWithMembers {
  id: number;
  identifier: string;
  name: string;
  description: { raw: string; html: string } | null;
  public: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  dueDate?: string;
  admins: ProjectMember[];
  members: ProjectMember[];
  workPackagesCount: number;
  filesCount: number;
  is_urgent?: boolean;
  is_following?: boolean;
  customFields?: {
    startDate?: string;
    dueDate?: string;
    partileContractante?: string;
    nrInregistrare?: string;
    dataSemnarii?: string;
    durataContract?: string;
    pretContract?: string;
  };
  _links: {
    self: { href: string };
    parent: { href: string; title: string } | null;
    status: { href: string; title: string };
  };
}

type SortField = 'name' | 'dueDate' | 'admins' | 'members' | 'workPackagesCount' | 'filesCount' | 'partileContractante';
type SortDirection = 'asc' | 'desc';

export function ProjectsTablePage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectWithMembers[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canCreate, setCanCreate] = useState(false);

  // Search and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadProjects = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [projectsResponse, createPermission] = await Promise.all([
        apiGet<HalCollection<ProjectWithMembers>>('/auth/data/projects/accessible'),
        canCreateProject(),
      ]);

      setProjects(projectsResponse._embedded?.elements || []);
      setCanCreate(createPermission);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  // Filter and sort projects
  const filteredAndSortedProjects = useMemo(() => {
    let result = [...projects];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.admins.some(a => a.name.toLowerCase().includes(query)) ||
        p.members.some(m => m.name.toLowerCase().includes(query))
      );
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name, 'ro');
          break;
        case 'dueDate':
          const dateA = a.customFields?.dueDate ? new Date(a.customFields.dueDate).getTime() : 0;
          const dateB = b.customFields?.dueDate ? new Date(b.customFields.dueDate).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case 'partileContractante':
          const partiA = a.customFields?.partileContractante || '';
          const partiB = b.customFields?.partileContractante || '';
          comparison = partiA.localeCompare(partiB, 'ro');
          break;
        case 'admins':
          const adminA = a.admins.map(m => m.name).join(', ');
          const adminB = b.admins.map(m => m.name).join(', ');
          comparison = adminA.localeCompare(adminB, 'ro');
          break;
        case 'members':
          comparison = a.members.length - b.members.length;
          break;
        case 'workPackagesCount':
          comparison = (a.workPackagesCount || 0) - (b.workPackagesCount || 0);
          break;
        case 'filesCount':
          comparison = (a.filesCount || 0) - (b.filesCount || 0);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [projects, searchQuery, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedProjects.length / pageSize);
  const paginatedProjects = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAndSortedProjects.slice(start, start + pageSize);
  }, [filteredAndSortedProjects, currentPage, pageSize]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortDirection === 'asc'
      ? <ChevronUp className="w-4 h-4 text-primary-600" />
      : <ChevronDown className="w-4 h-4 text-primary-600" />;
  };

  const handleProjectClick = (project: ProjectWithMembers) => {
    navigate(`/projects/${project.id}`);
  };

  if (isLoading) {
    return <LoadingState message={t('common.loading')} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-primary-500 text-white">
            <FolderKanban className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Proiecte
            </h1>
            <p className="text-gray-600 mt-1">
              Toate proiectele la care aveți acces
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full font-medium self-center">
            {filteredAndSortedProjects.length} {filteredAndSortedProjects.length === 1 ? 'proiect' : 'proiecte'}
          </span>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={loadProjects}
          >
            {t('common.refresh')}
          </Button>
          {canCreate && (
            <Button
              variant="primary"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => navigate('/projects/new')}
            >
              {t('projects.addProject')}
            </Button>
          )}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* Table Card */}
      <Card className="overflow-hidden">
        {/* Search and page size */}
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Caută proiecte..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Afișează</span>
            <SearchableSelect
              className="w-24"
              clearable={false}
              options={[
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
              ]}
              value={pageSize}
              onChange={(v) => {
                setPageSize(Number(v));
                setCurrentPage(1);
              }}
            />
            <span className="text-sm text-gray-500">înregistrări</span>
          </div>
        </div>

        {/* Table */}
        {filteredAndSortedProjects.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon="folder"
              title="Nu există proiecte"
              description={searchQuery ? "Nu s-au găsit proiecte care să corespundă căutării." : "Nu aveți acces la niciun proiect."}
            />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-2">
                        Denumire proiect
                        <SortIcon field="name" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('partileContractante')}
                    >
                      <div className="flex items-center gap-2">
                        Părți contractante
                        <SortIcon field="partileContractante" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('dueDate')}
                    >
                      <div className="flex items-center gap-2">
                        Data limită
                        <SortIcon field="dueDate" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('admins')}
                    >
                      <div className="flex items-center gap-2">
                        Admin proiect
                        <SortIcon field="admins" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('members')}
                    >
                      <div className="flex items-center gap-2">
                        Membri proiect
                        <SortIcon field="members" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('workPackagesCount')}
                    >
                      <div className="flex items-center justify-center gap-2">
                        Activități
                        <SortIcon field="workPackagesCount" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('filesCount')}
                    >
                      <div className="flex items-center justify-center gap-2">
                        Fișiere
                        <SortIcon field="filesCount" />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedProjects.map((project) => (
                    <tr
                      key={project.id}
                      onClick={() => handleProjectClick(project)}
                      className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                        project.is_urgent ? 'bg-danger-50/60 border-l-4 border-danger-500' : ''
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            project.is_urgent ? 'bg-danger-100' : 'bg-primary-100'
                          }`}>
                            <FolderKanban className={`w-4 h-4 ${project.is_urgent ? 'text-danger-600' : 'text-primary-600'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900 hover:text-primary-600">
                                {project.name}
                              </span>
                              {project.is_urgent && (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-danger-100 text-danger-700 border border-danger-200"
                                  title="Proiect marcat ca urgent"
                                >
                                  <AlertTriangle className="w-3 h-3" />
                                  Urgent
                                </span>
                              )}
                              {project.is_following && (
                                <span
                                  className="inline-flex items-center gap-1 text-primary-500"
                                  title="Urmăriți acest proiect"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 space-y-0.5 mt-1">
                              {project.customFields?.nrInregistrare && (
                                <div><span className="font-semibold text-gray-600">Nr.:</span> {project.customFields.nrInregistrare}</div>
                              )}
                              {project.customFields?.dataSemnarii && (
                                <div><span className="font-semibold text-gray-600">Data semnării:</span> {format(parseISO(project.customFields.dataSemnarii), 'dd.MM.yyyy', { locale: ro })}</div>
                              )}
                              {project.customFields?.pretContract && (
                                <div><span className="font-semibold text-gray-600">Valoare:</span> {new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(parseFloat(project.customFields.pretContract))}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                        <div className="whitespace-pre-line" title={project.customFields?.partileContractante || ''}>
                          {project.customFields?.partileContractante || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {project.customFields?.dueDate
                          ? format(parseISO(project.customFields.dueDate), 'dd MMM yyyy', { locale: ro })
                          : '-'}
                      </td>
                      <td className="px-6 py-4">
                        {project.admins.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {project.admins.map((admin, idx) => (
                              <span
                                key={admin.id}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                              >
                                {admin.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td
                        className="px-6 py-4 cursor-pointer hover:bg-blue-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/projects/${project.id}#members`);
                        }}
                        title="Click pentru a vedea membrii"
                      >
                        {project.members.length > 0 ? (
                          /* Îi afișăm pe TOȚI. Înainte se arătau doar primii 3 + „+N alții",
                             ceea ce părea că nu se potrivește cu lista din pagina de detaliu
                             a proiectului (care i-a arătat mereu pe toți). Maximul real e de
                             6 membri pe proiect, deci încap fără să crească exagerat rândul. */
                          <div className="flex flex-wrap gap-1">
                            {project.members.map((member) => (
                              <span
                                key={member.id}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
                              >
                                {member.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td
                        className="px-6 py-4 text-center cursor-pointer hover:bg-blue-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/projects/${project.id}#gantt`);
                        }}
                        title="Click pentru a vedea activitățile"
                      >
                        <div className="flex items-center justify-center gap-1">
                          <ListTodo className="w-4 h-4 text-primary-500" />
                          <span className="text-sm font-medium text-gray-700">
                            {project.workPackagesCount || 0}
                          </span>
                        </div>
                      </td>
                      <td
                        className="px-6 py-4 text-center cursor-pointer hover:bg-blue-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/projects/${project.id}#files`);
                        }}
                        title="Click pentru a vedea fișierele"
                      >
                        <div className="flex items-center justify-center gap-1">
                          <FileText className="w-4 h-4 text-orange-500" />
                          <span className="text-sm font-medium text-gray-700">
                            {project.filesCount || 0}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-500">
                Afișare {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredAndSortedProjects.length)} din {filteredAndSortedProjects.length} proiecte
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Prima
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <span className="px-3 py-1.5 text-sm bg-primary-50 text-primary-700 border border-primary-200 rounded-lg font-medium">
                  {currentPage} / {totalPages || 1}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Următor
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ultima
                </button>
              </div>
            </div>
          </>
        )}
      </Card>

    </div>
  );
}
